import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { Calendar } from "react-date-range";
import { useTranslation } from "react-i18next";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "./Session.css";
import "./AddSessionDialog.css";
import { currentTimeValue, openNativeTimePicker } from "../utils/sessionNow";

const DURATION_PRESETS = [45, 60, 90, 120];

const daysOfWeek = [
  { value: 0, label: "Sunday", short: "Sun" },
  { value: 1, label: "Monday", short: "Mon" },
  { value: 2, label: "Tuesday", short: "Tue" },
  { value: 3, label: "Wednesday", short: "Wed" },
  { value: 4, label: "Thursday", short: "Thu" },
  { value: 5, label: "Friday", short: "Fri" },
  { value: 6, label: "Saturday", short: "Sat" },
];

const SeasonalSession = ({
  open,
  setopen,
  handleadd,
  type,
  selectedgrp,
  allowPastDates = false,
}) => {
  const [sessionDuration, setSessionDuration] = useState(90);
  const [time, settime] = useState("");
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [selectedDays, setSelectedDays] = useState([]);
  const timeInputRef = useRef(null);

  const { t } = useTranslation();

  const applyNow = () => {
    const now = new Date();
    setStartDate(now);
    settime(currentTimeValue(now));
  };

  useEffect(() => {
    if (!open) {
      setSessionDuration(90);
      settime("");
      setStartDate(new Date());
      setEndDate(new Date());
      setSelectedDays([]);
    }
  }, [open]);

  const handleClose = () => setopen(false);

  const handleDayToggle = (dayValue) => {
    setSelectedDays((prev) =>
      prev.includes(dayValue)
        ? prev.filter((d) => d !== dayValue)
        : [...prev, dayValue]
    );
  };

  const handleSubmit = () => {
    if (!type || !type.te_gen_mode_id) {
      console.error("⚠️ SeasonalSession - Cannot add session: type or te_gen_mode_id is null");
      return;
    }

    if (!time) {
      alert(t("timeRequired") || "Please select a start time");
      return;
    }

    if (selectedDays.length === 0) {
      alert(t("selectAtLeastOneDay") || "Please select at least one day of the week");
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) {
      alert(t("startDateMustBeBeforeEndDate") || "Start date must be before end date");
      return;
    }

    if (!selectedgrp || !selectedgrp.groupDetails || !selectedgrp.groupDetails.gr_id) {
      console.error("⚠️ SeasonalSession - Cannot add session: group ID is missing");
      alert(t("selectGroupFirst") || "Please select a group first");
      return;
    }

    handleadd(
      sessionDuration,
      time,
      startDate,
      endDate,
      selectedDays,
      type.te_gen_mode_id,
      selectedgrp.groupDetails.gr_id
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="seasonal-session-title"
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "rgba(var(--brand-primary-rgb), 0.35)" },
        },
      }}
    >
      <Box className="asd-modal-box asd-modal-box--wide seasonal-session-modal session-add-dialog home-theme-root">
        <div className="asd">
          <header className="asd__header">
            <div className="asd__hero" aria-hidden>
              <DateRangeOutlinedIcon fontSize="small" />
            </div>
            <div className="asd__header-text">
              <p className="asd__eyebrow">{t("seasonal") || "Seasonal"}</p>
              <h2 id="seasonal-session-title" className="asd__title">
                {t("addseasonalsessions") || "Add Seasonal Sessions"}
              </h2>
              <p className="asd__hint">
                {t("seasonalDescription") ||
                  "Create multiple sessions with recurring dates"}
              </p>
            </div>
            <button
              type="button"
              className="asd__close"
              onClick={handleClose}
              aria-label={t("cancel") || "Cancel"}
            >
              <CloseIcon fontSize="small" />
            </button>
          </header>

          <div className="asd__body">
            <section className="asd__section">
              <p className="asd__label">{t("startDate") || "Start Date"}</p>
              <div className="asd__field-shell">
                <div className="asd__calendar session-add-dialog__calendar">
                  <Calendar
                    date={startDate}
                    onChange={(e) => setStartDate(e)}
                    showMonthAndYearPickers={allowPastDates}
                  />
                </div>
              </div>
            </section>

            <section className="asd__section">
              <p className="asd__label">{t("endDate") || "End Date"}</p>
              <div className="asd__field-shell">
                <div className="asd__calendar session-add-dialog__calendar">
                  <Calendar
                    date={endDate}
                    onChange={(e) => setEndDate(e)}
                    showMonthAndYearPickers={allowPastDates}
                  />
                </div>
              </div>
            </section>

            <section className="asd__section">
              <p className="asd__label">{t("daysOfWeek") || "Days of Week"}</p>
              <div
                className="asd__day-grid"
                role="group"
                aria-label={t("daysOfWeek") || "Days of Week"}
              >
                {daysOfWeek.map((day) => {
                  const active = selectedDays.includes(day.value);
                  return (
                    <button
                      key={day.value}
                      type="button"
                      className={`asd__day${active ? " is-active" : ""}`}
                      aria-pressed={active}
                      title={t(`day${day.value}`) || day.label}
                      onClick={() => handleDayToggle(day.value)}
                    >
                      {(t(`dayShort${day.value}`) || day.short).slice(0, 2)}
                    </button>
                  );
                })}
              </div>
            </section>

            <div className="asd__row">
              <section className="asd__section">
                <div className="asd__label-row">
                  <label className="asd__label" htmlFor="asd-seasonal-time">
                    {t("starttime") || "Start time"}
                  </label>
                  <button
                    type="button"
                    className="asd__chip asd__chip--now"
                    onClick={applyNow}
                  >
                    {t("now") || "Now"}
                  </button>
                </div>
                <div className="asd__time-controls">
                  <input
                    id="asd-seasonal-time"
                    ref={timeInputRef}
                    className="asd__input asd__input--time"
                    type="time"
                    value={time}
                    onChange={(e) => settime(e.target.value)}
                    step={300}
                    required
                  />
                  <button
                    type="button"
                    className="asd__clock-btn"
                    onClick={() => openNativeTimePicker(timeInputRef.current)}
                    aria-label={t("openTimePicker") || "Open time picker"}
                    title={t("openTimePicker") || "Open time picker"}
                  >
                    <AccessTimeIcon fontSize="small" />
                  </button>
                </div>
              </section>

              <section className="asd__section">
                <label className="asd__label" htmlFor="asd-seasonal-duration">
                  {t("duration") || "Duration"} ({t("min") || "min"})
                </label>
                <input
                  id="asd-seasonal-duration"
                  className="asd__input"
                  type="number"
                  min={1}
                  value={sessionDuration}
                  onChange={(e) => setSessionDuration(e.target.value)}
                />
              </section>
            </div>

            <section className="asd__section">
              <p className="asd__label">{t("quickDuration") || "Quick pick"}</p>
              <div className="asd__duration-chips" role="group">
                {DURATION_PRESETS.map((mins) => (
                  <button
                    key={mins}
                    type="button"
                    className={`asd__chip${
                      Number(sessionDuration) === mins ? " is-active" : ""
                    }`}
                    onClick={() => setSessionDuration(mins)}
                  >
                    {mins} {t("min") || "min"}
                  </button>
                ))}
              </div>
            </section>
          </div>

          <div className="asd__actions">
            <button type="button" className="asd__btn asd__btn--ghost" onClick={handleClose}>
              {t("cancel") || "Cancel"}
            </button>
            <button type="button" className="asd__btn asd__btn--primary" onClick={handleSubmit}>
              {t("addseasonalsessions") || "Add Seasonal Sessions"}
            </button>
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default SeasonalSession;
